import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    companySize: '',
    industry: '',
    currentChallenge: '',
    projectTimeline: '',
    budget: '',
    message: '',
    gdprConsent: false,
    marketingConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'sme', label: 'SME (51-250 employees)' },
    { value: 'enterprise', label: 'Enterprise (251-1000 employees)' },
    { value: 'large-enterprise', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industryOptions = [
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'technology', label: 'Technology' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'other', label: 'Other' }
  ];

  const challengeOptions = [
    { value: 'data-governance', label: 'Data Governance & Compliance' },
    { value: 'analytics-strategy', label: 'Analytics Strategy' },
    { value: 'data-quality', label: 'Data Quality Issues' },
    { value: 'team-training', label: 'Team Training & Capability Building' },
    { value: 'technology-selection', label: 'Technology Selection' },
    { value: 'roi-measurement', label: 'ROI Measurement' },
    { value: 'change-management', label: 'Change Management' },
    { value: 'other', label: 'Other Challenge' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: 'short-term', label: 'Short-term (1-3 months)' },
    { value: 'medium-term', label: 'Medium-term (3-6 months)' },
    { value: 'long-term', label: 'Long-term (6+ months)' },
    { value: 'exploring', label: 'Just exploring options' }
  ];

  const budgetOptions = [
    { value: 'under-25k', label: 'Under €25,000' },
    { value: '25k-50k', label: '€25,000 - €50,000' },
    { value: '50k-100k', label: '€50,000 - €100,000' },
    { value: '100k-250k', label: '€100,000 - €250,000' },
    { value: 'over-250k', label: 'Over €250,000' },
    { value: 'not-determined', label: 'Not yet determined' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        jobTitle: '',
        phone: '',
        companySize: '',
        industry: '',
        currentChallenge: '',
        projectTimeline: '',
        budget: '',
        message: '',
        gdprConsent: false,
        marketingConsent: false
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <section id="contact-form" className="py-20 bg-surface border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-2xl shadow-elevation p-8 text-center transition-all duration-500 hover:shadow-2xl">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300 hover:bg-success/20 hover:scale-110">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <h3 className="text-2xl font-semibold text-primary mb-4">
              Thank You for Reaching Out!
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              We've received your message and will respond within 24 hours. Our team is already reviewing your requirements to provide the most relevant insights.
            </p>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>What happens next:</strong>
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2 transition-all duration-300 hover:scale-105 hover:text-secondary">
                  <Icon name="Clock" size={16} className="text-secondary" />
                  <span>Response within 24 hours</span>
                </div>
                <div className="flex items-center space-x-2 transition-all duration-300 hover:scale-105 hover:text-secondary">
                  <Icon name="User" size={16} className="text-secondary" />
                  <span>Assigned expert contact</span>
                </div>
                <div className="flex items-center space-x-2 transition-all duration-300 hover:scale-105 hover:text-secondary">
                  <Icon name="Calendar" size={16} className="text-secondary" />
                  <span>Consultation scheduling</span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setSubmitStatus(null)}
              className="mt-6 transition-all duration-300 hover:bg-secondary/10 hover:border-secondary hover:text-secondary hover:scale-105 active:scale-95 focus:ring-2 focus:ring-secondary focus:ring-offset-2"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="py-20 bg-surface border-b border-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-secondary/20 hover:scale-110 hover:rotate-6">
              <Icon name="MessageSquare" size={20} className="text-secondary" />
            </div>
            <span className="text-sm font-semibold text-secondary uppercase tracking-wider">General Inquiry</span>
          </div>
        </div>
        
        <div className="text-center mb-12">
          <h2 className="text-section-title text-primary mb-4">
            General Inquiry & Questions
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Have questions or need more information? Send us a message and we'll connect you with the right expert.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-elevation p-8 transition-all duration-500 hover:shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center transition-colors duration-300 hover:text-secondary">
                <Icon name="User" size={20} className="mr-2 transition-transform duration-300 hover:scale-110" />
                Contact Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  type="text"
                  required
                  value={formData?.firstName}
                  onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                  placeholder="Enter your first name"
                />
                <Input
                  label="Last Name"
                  type="text"
                  required
                  value={formData?.lastName}
                  onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                  placeholder="Enter your last name"
                />
                <Input
                  label="Email Address"
                  type="email"
                  required
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  placeholder="your.email@company.com"
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  placeholder="+353 1 234 5678"
                />
              </div>
            </div>

            {/* Company Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
                <Icon name="Building" size={20} className="mr-2" />
                Company Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Company Name"
                  type="text"
                  required
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                  placeholder="Your company name"
                />
                <Input
                  label="Job Title"
                  type="text"
                  required
                  value={formData?.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
                  placeholder="Your role/position"
                />
                <Select
                  label="Company Size"
                  options={companySizeOptions}
                  value={formData?.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                  placeholder="Select company size"
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  placeholder="Select your industry"
                />
              </div>
            </div>

            {/* Project Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
                <Icon name="Target" size={20} className="mr-2" />
                Project Details
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Select
                  label="Primary Challenge"
                  options={challengeOptions}
                  value={formData?.currentChallenge}
                  onChange={(value) => handleInputChange('currentChallenge', value)}
                  placeholder="What's your main challenge?"
                />
                <Select
                  label="Project Timeline"
                  options={timelineOptions}
                  value={formData?.projectTimeline}
                  onChange={(value) => handleInputChange('projectTimeline', value)}
                  placeholder="When do you need to start?"
                />
                <Select
                  label="Budget Range"
                  options={budgetOptions}
                  value={formData?.budget}
                  onChange={(value) => handleInputChange('budget', value)}
                  placeholder="Select budget range"
                  className="md:col-span-2"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Additional Details
              </label>
              <textarea
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                placeholder="Tell us more about your specific requirements, current challenges, or any questions you have..."
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
            </div>

            {/* Consent Checkboxes */}
            <div className="space-y-4 border-t border-border pt-6">
              <Checkbox
                label="I consent to processing of my personal data in accordance with GDPR regulations"
                description="Required to process your inquiry and provide relevant information"
                required
                checked={formData?.gdprConsent}
                onChange={(e) => handleInputChange('gdprConsent', e?.target?.checked)}
              />
              <Checkbox
                label="I would like to receive updates about data strategy insights and industry trends"
                description="Optional - you can unsubscribe at any time"
                checked={formData?.marketingConsent}
                onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
              />
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="left"
                className="bg-secondary hover:bg-secondary/90 flex-1"
                disabled={!formData?.gdprConsent}
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>
              <Button
                type="button"
                variant="outline"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => document.getElementById('consultation-booking')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Skip Form - Book Call
              </Button>
            </div>

            {submitStatus === 'error' && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 text-center">
                <p className="text-error">
                  There was an error sending your message. Please try again or contact us directly.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;